import React from 'react';
import { useSelector } from 'react-redux';
import { Popover, PopoverTrigger, PopoverContent, Button } from '@heroui/react';
import { Info } from 'lucide-react';
import { selectActiveActivityTypes } from '../refsSlice.js';
import { STATUS_ACCENT } from './EventChip.js';
import { pickCalendarBootstrap } from '../../calendar-common/viewStoreKeys.js';
import { isFlightFollowingEnabled } from '../../calendar-common/flightFollowing.js';

/**
 * EventChipLegend — popover-triggered cheat-sheet for the event chip states.
 * Kept in sync with `EventChip.resolveChipStyle`. Inline banner didn't fit
 * cleanly on one row — popover frees the page and lets the legend live
 * behind an "i" button, shown only on demand.
 *
 * Two modes:
 *   - Default — Flight / Maintenance status colors.
 *   - Activity Type Color Scheme ON (Rem30 Task 6) — one swatch per activity
 *     type, the 3px status accent-bar legend, and Maintenance (which stays
 *     status-colored regardless of the scheme).
 */

const flightStates = [
	{
		label: 'Not Started',
		className: 'bg-cal-flight-not-started-bg border-cal-flight-not-started-border'
	},
	{
		label: 'Waiting (Not Started)',
		className:
			'bg-cal-flight-not-started-bg border-cal-flight-not-started-border chip-stripe-yellow'
	},
	{
		label: 'In Progress (Checked Out)',
		className: 'bg-cal-flight-in-progress-bg border-cal-flight-in-progress-border'
	},
	{
		label: 'Completed (Checked In)',
		className: 'bg-cal-flight-completed-bg border-cal-flight-completed-border'
	}
];

// Flight following only (org enableTrackFlight) — appended when the feature
// is on for the current org.
const overdueState = {
	label: 'Overdue (past expected arrival)',
	className: 'bg-[#FBD9DB] border-[#E5484D]'
};

const maintenanceStates = [
	{
		label: 'Not Started',
		className: 'bg-cal-maint-bg border-cal-maint-border'
	},
	{
		label: 'In Progress (Checked Out)',
		className: 'bg-cal-maint-bg border-cal-maint-border chip-stripe-blue-on-gray'
	},
	{
		label: 'Completed (Checked In)',
		className: 'bg-cal-maint-bg border-cal-maint-border chip-stripe-green-on-gray'
	}
];

// 3px left accent-bar legend — mirrors `EventChip.STATUS_ACCENT`. Shown only
// when the Activity Type Color Scheme is on (status no longer drives the
// chip background, so the accent bar carries event phase).
const statusAccents = [
	{ label: 'Not Started', accent: STATUS_ACCENT.notStarted },
	{ label: 'In Progress (Checked Out)', accent: STATUS_ACCENT.inProgress },
	{ label: 'Completed (Checked In)', accent: STATUS_ACCENT.completed }
];

function LegendRow({ label, className }) {
	return (
		<div className="flex items-center gap-2">
			<span
				className={`inline-block w-8 h-4 rounded-sm border shrink-0 ${className}`}
				aria-hidden="true"
			/>
			<span className="text-xs text-default-700">{label}</span>
		</div>
	);
}

/** Row whose swatch carries an inline style (activity-type color / accent bar). */
function LegendStyleRow({ label, style }) {
	return (
		<div className="flex items-center gap-2 min-w-0">
			<span
				className="inline-block w-8 h-4 rounded-sm border border-black/10 shrink-0"
				style={style}
				aria-hidden="true"
			/>
			<span className="text-xs text-default-700 truncate">{label}</span>
		</div>
	);
}

function LegendSection({ title, items }) {
	return (
		<div className="flex flex-col gap-1.5">
			<span className="text-[10px] font-semibold uppercase tracking-wide text-default-500">
				{title}
			</span>
			{items.map(item => (
				<LegendRow key={item.label} label={item.label} className={item.className} />
			))}
		</div>
	);
}

/**
 * @param {Object} [props]
 * @param {boolean} [props.activityTypeColorScheme] Override the scheme flag. When
 *   omitted, reads the month store (`state.calendarMonth.filters`) as before.
 * @param {Object<string,Object>} [props.activeActivityTypes] Override the active
 *   activity-types map. When omitted, reads the month refs slice. Other calendar
 *   views (week/day/…) pass their own store values so the legend is view-agnostic.
 */
function EventChipLegend({ activityTypeColorScheme: activityTypeColorSchemeProp, activeActivityTypes: activeActivityTypesProp } = {}) {
	// Always read the month-store defaults (hooks can't be conditional); props,
	// when provided, take precedence so week/day views can drive this legend.
	const monthActivityTypeColorScheme = useSelector(state =>
		Boolean(state?.calendarMonth?.filters?.activityTypeColorScheme)
	);
	const monthActiveActivityTypes = useSelector(selectActiveActivityTypes);
	const flightFollowing = useSelector(state =>
		isFlightFollowingEnabled(pickCalendarBootstrap(state)?.organizationSettings)
	);
	const flightItems = flightFollowing ? [...flightStates, overdueState] : flightStates;
	const activityTypeColorScheme =
		activityTypeColorSchemeProp !== undefined
			? activityTypeColorSchemeProp
			: monthActivityTypeColorScheme;
	const activeActivityTypes =
		activeActivityTypesProp !== undefined ? activeActivityTypesProp : monthActiveActivityTypes;
	const activityTypeList = Object.values(activeActivityTypes || {});
	// Activity-type mode only when the scheme is on AND the org has types
	// (mirrors the checkbox visibility gate in `MonthV2Filter`).
	const showActivityScheme = activityTypeColorScheme && activityTypeList.length > 0;

	return (
		<Popover placement="top-start" offset={6}>
			<PopoverTrigger>
				<Button size="sm" variant="bordered" startContent={<Info className="w-3.5 h-3.5" />}>
					Legend
				</Button>
			</PopoverTrigger>
			<PopoverContent>
				{/* Activity-scheme mode: widen slightly for 2-col list + cap height so a
				    long type list never overflows the viewport. */}
				<div className={`flex flex-col gap-4 p-3 ${showActivityScheme ? 'w-[min(320px,calc(100vw-1rem))] max-h-[min(70vh,480px)] overflow-y-auto' : 'w-[min(280px,calc(100vw-1rem))]'}`}>
					{showActivityScheme ? (
						<>
							<div className="flex flex-col gap-1.5">
								<span className="text-[10px] font-semibold uppercase tracking-wide text-default-500">
									Activity Type
								</span>
								{/* 2-col grid halves the height; scroll fallback on the outer
								    wrapper handles very long lists. */}
								<div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
									{activityTypeList.map(at => (
										<LegendStyleRow
											key={at.id}
											label={at.name || 'Untitled'}
											style={{ backgroundColor: at.color || '#ececec' }}
										/>
									))}
								</div>
							</div>
							<div className="flex flex-col gap-1.5">
								<span className="text-[10px] font-semibold uppercase tracking-wide text-default-500">
									Status (left bar)
								</span>
								{statusAccents.map(s => (
									<LegendStyleRow
										key={s.label}
										label={s.label}
										style={{ boxShadow: `inset 3px 0 0 0 ${s.accent}` }}
									/>
								))}
							</div>
							<LegendSection title="Maintenance" items={maintenanceStates} />
						</>
					) : (
						<>
							<LegendSection title="Flight" items={flightItems} />
							<LegendSection title="Maintenance" items={maintenanceStates} />
						</>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}

export default EventChipLegend;
